import { escapeRegExp } from './common';

// Carried over from the original hot fix: long package names are left alone rather than
// risking an aggressive rewrite of a title that is mostly package text.
const MAX_PACKAGE_NAME_LENGTH = 30;
const PACKAGE_SEPARATOR = 'w/';
const LOWERCASE_W = 119;
const UPPERCASE_W = 87;

/**
 * True when `title` holds at least two `w/` separators, i.e. when it could possibly carry
 * a duplicate. Cheap allocation-free pre-check: this runs for every listing on every
 * render, so the regex below is only built for the titles that can actually match.
 */
const hasDuplicateSeparators = (title) => {
    let count = 0;
    let index = title.indexOf('/');

    while (index !== -1) {
        // `charCodeAt(-1)` is NaN, which matches neither code, so index 0 is handled.
        const previousCharCode = title.charCodeAt(index - 1);

        if (previousCharCode === LOWERCASE_W || previousCharCode === UPPERCASE_W) {
            count += 1;

            if (count > 1) {
                return true;
            }
        }

        index = title.indexOf('/', index + 1);
    }

    return false;
};

/**
 * DE336743: LSC sometimes repeats the `w/ <package name>` trim segment inside a listing
 * title (e.g. `Used 2024 Toyota Camry w/ GR Wheel Package (TMS) w/ GR Wheel Package (TMS)`).
 * Keeps the first occurrence and drops every later one.
 *
 * @param {object} listing an inventory listing, read for `title` and `packages`
 * @returns {string} the title with duplicated package segments removed
 */
const removeDuplicatePackageFromTitle = ({ title, packages } = {}) => {
    const packageName = packages?.[0]?.name;

    // A non-string or blank name would reduce the pattern below to a bare `w/\s*` and
    // strip every `w/` segment in the title, so it has to bail here.
    if (typeof packageName !== 'string'
        || typeof title !== 'string'
        || !packageName.trim()
        || packageName.length >= MAX_PACKAGE_NAME_LENGTH
        || !hasDuplicateSeparators(title)) {
        return title;
    }

    // Package names are upstream data and routinely contain regex metacharacters
    // (e.g. `GR Wheel Package (TMS)`), so they must be escaped before being interpolated
    // into a pattern -- otherwise `new RegExp` throws `Invalid regular expression` and
    // takes down every listing on the page. Case-insensitive because the title and
    // `packages[].name` come from different LSC fields and do not always agree.
    const packageRegex = new RegExp(`${PACKAGE_SEPARATOR}\\s*${escapeRegExp(packageName)}`, 'gi');
    const lowerTitle = title.toLowerCase();

    // A longer package whose name starts with this one (`Sport Package` vs
    // `Sport Package Plus`) also matches the pattern, so its suffix is used to tell the
    // two apart and keep the distinct package intact.
    const lowerName = packageName.toLowerCase();
    const longerPackageSuffixes = packages.length > 1
        ? packages
            .map((vehiclePackage) => vehiclePackage?.name)
            .filter((name) => typeof name === 'string'
                && name.length > packageName.length
                && name.toLowerCase().startsWith(lowerName))
            .map((name) => name.slice(packageName.length).toLowerCase())
        : [];

    // End of the `w/ ...` segment a match belongs to: the next separator, or the end of
    // the title. Trailing whitespace stays outside the segment so that removing one still
    // leaves the separator between its neighbours.
    const getSegmentEnd = (matchEnd) => {
        const nextSeparator = lowerTitle.indexOf(PACKAGE_SEPARATOR, matchEnd);
        let segmentEnd = nextSeparator === -1 ? title.length : nextSeparator;

        while (segmentEnd > matchEnd && /\s/.test(title[segmentEnd - 1])) {
            segmentEnd -= 1;
        }

        return segmentEnd;
    };

    const occurrences = [];
    let match = packageRegex.exec(title);

    while (match !== null) {
        const matchEnd = match.index + match[0].length;

        if (!longerPackageSuffixes.some((suffix) => lowerTitle.startsWith(suffix, matchEnd))) {
            const segmentEnd = getSegmentEnd(matchEnd);

            occurrences.push({
                index: match.index,
                matchEnd,
                segmentEnd,
                tail: lowerTitle.slice(matchEnd, segmentEnd).trim(),
            });
        }

        match = packageRegex.exec(title);
    }

    if (occurrences.length < 2) {
        return title;
    }

    const [{ tail: keptTail }] = occurrences;

    // Splice out every occurrence after the first, back to front so the earlier offsets
    // stay valid. When a duplicate is trailed by the same text as the occurrence being
    // kept, that text is part of the repeated segment and goes with it; otherwise only the
    // matched `w/ <package>` is removed so unrelated title text survives. Only the
    // whitespace directly in front of a removal is absorbed, leaving the spacing elsewhere
    // in the title untouched.
    const dedupedTitle = occurrences.slice(1).reverse().reduce((result, occurrence) => {
        const end = occurrence.tail === keptTail ? occurrence.segmentEnd : occurrence.matchEnd;
        let start = occurrence.index;

        while (start > 0 && /\s/.test(result[start - 1])) {
            start -= 1;
        }

        return result.slice(0, start) + result.slice(end);
    }, title);

    return dedupedTitle.trim();
};

export default removeDuplicatePackageFromTitle;
